import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, useHistory } from 'react-router-dom';
import { Provider as QueryProvider } from 'urql';
import * as Castle from '@castleio/castle-js';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { theme as MUITheme } from 'theme/mui';
import client from 'api/urql';
import Themer from 'theme';
import { connect, useSelector } from 'react-redux';
import { get } from 'lodash';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Adapter } from 'utilities/date/datepickerLocalizationAdapter';
import { IS_FRAMED } from 'utilities/integration/rails';
import env from 'utilities/env';
import { Settings } from 'luxon';
import { LicenseInfo } from '@mui/x-license';
import { initializeDatadogRum } from 'utilities/datadog/rum';
import { getCurrentUser } from 'selectors';

const supportsHistory = 'pushState' in window.history;

const RailsNavigationController = ({ railsURL, children }) => {
  const history = useHistory();
  useEffect(() => {
    // Only trigger in iFrame and with "react-native-nav" LD flag
    if (railsURL && IS_FRAMED) {
      history.replace(railsURL);
    }
  }, [railsURL]);

  return children;
};
RailsNavigationController.propTypes = {
  railsURL: PropTypes.string,
  children: PropTypes.node.isRequired
};
RailsNavigationController.defaultProps = {
  railsURL: null
};

const mapStateToProps = (state) => {
  const { postMessage } = state;
  const railsURL = get(postMessage, 'PAGE_CHANGE.RAILS_NAVIGATION.data.url', null);
  return {
    railsURL
  };
};
const RailsNavigation = connect(mapStateToProps)(RailsNavigationController);

const RouterWrapper = ({ children }) => (
  <BrowserRouter
    forceRefresh={!supportsHistory}
    basename={process.env.PUBLIC_URL}
  >
    {children}
  </BrowserRouter>
);

RouterWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

const QueryWrapper = ({ children }) => (
  <QueryProvider value={client}>
    {children}
  </QueryProvider>
);

QueryWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

Settings.defaultWeekSettings = {
  firstDay: 7,
  minimalDays: 1,
  weekend: [6, 7],
};

const AppWrapper = ({ children }) => {
  const user = useSelector(getCurrentUser);

  const dataDogEnabled = env('DATADOG_ENABLED') === 'true';
  if (dataDogEnabled) {
    // Initialize Datadog RUM early in the application lifecycle
    // This will re-run whenever user changes (login/logout)
    initializeDatadogRum(user);
  }

  Castle.configure({ pk: env('CASTLE_PUB_KEY') });
  LicenseInfo.setLicenseKey(env('MUI_LICENSE_KEY'));
  return (
    <QueryWrapper>
      <Themer>
        <MUIThemeProvider theme={MUITheme}>
          <LocalizationProvider dateAdapter={Adapter}>
            <RouterWrapper>
              <RailsNavigation>
                {children}
              </RailsNavigation>
            </RouterWrapper>
          </LocalizationProvider>
        </MUIThemeProvider>
      </Themer>
    </QueryWrapper>
  );
};

AppWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppWrapper;
